import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../store";
import { toast } from "react-toastify";
import { fetchShippingBoxType, createShippingBoxType, updateShippingBoxType, deleteShippingBoxType } from "./shippingBoxTypeAsyncThunk";
import { DeleteCourier } from "../../../types/CourierTypes";
import { BoxType } from "../../../types/ShippingBoxTypes";
import { RequestResults } from "../../common/RequestStatus";

interface ShippingBoxTypeState {
  shippingBoxTypes: BoxType[];
  status: RequestResults;
  loading: boolean;
  error: string | null;
}

const initialState: ShippingBoxTypeState = {
  shippingBoxTypes: [],
  status: RequestResults.idle,
  loading: false,
  error: null,
};

// Slice
const shippingBoxTypeSlice = createSlice({
  name: "shippingBoxType",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Fetch Shipping Box Type
    builder
      .addCase(fetchShippingBoxType.pending, (state) => {
        state.loading = true;
        state.status = RequestResults.pending;
        state.error = null;
      })
      .addCase(fetchShippingBoxType.fulfilled, (state, action: PayloadAction<BoxType[]>) => {
        state.status = RequestResults.success;
        state.shippingBoxTypes = [...action.payload];
        state.loading = false;
      })
      .addCase(fetchShippingBoxType.rejected, (state, action) => {
        state.status = RequestResults.failed;
        state.loading = false;
        toast.error(action.payload || "Failed to fetch shipping box type");
        state.error = action.payload || "Failed to fetch shipping box type";
      });

    // Add Shipping Box Type
    builder
      .addCase(createShippingBoxType.pending, (state) => {
        state.loading = true;
      })
      .addCase(createShippingBoxType.fulfilled, (state, action: PayloadAction<BoxType>) => {
        state.loading = false;
        state.shippingBoxTypes.push(action.payload);
      })
      .addCase(createShippingBoxType.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload || "Failed to add shipping box type");
        state.error = action.payload || "Failed to add shipping box type";
      });

    // Update Shipping Box Type
    builder
      .addCase(updateShippingBoxType.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateShippingBoxType.fulfilled, (state, action: PayloadAction<BoxType>) => {
        const index = state.shippingBoxTypes.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          state.shippingBoxTypes[index] = action.payload;
        }
        state.loading = false;
      })
      .addCase(updateShippingBoxType.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload || "Failed to update shipping box type");
        state.error = action.payload || "Failed to update shipping box type";
      });

    // Delete Shipping Box Type
    builder
      .addCase(deleteShippingBoxType.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteShippingBoxType.fulfilled, (state, action: PayloadAction<DeleteCourier>) => {
        const index = state.shippingBoxTypes.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          state.shippingBoxTypes = state.shippingBoxTypes.filter((item) => item.id !== action.payload.id);
        }
        state.loading = false;
      })
      .addCase(deleteShippingBoxType.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload || "Failed to delete shipping box type");
        state.error = action.payload || "Failed to delete shipping box type";
      });
  },
});

export const selectShippingBoxType = (state: RootState) => state.shippingBoxType;

export default shippingBoxTypeSlice.reducer;
